import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { type LastPriceEstimate, useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

/**
 * Entry point for a standard reservation. The parking selection and park-time
 * contract are already in the store from the search/details flow, so this only
 * hands off to the shared reservation flow — `ReservationFlowPage` creates the
 * reservation (via `useEnsurePendingReservation`) from that store state.
 *
 * Clearing the package intent here keeps a leftover price rule from a
 * not-completed package attempt from turning this standard reservation into a
 * package one. Mirror of {@link useStartPackageReservation}.
 *
 * The estimate the user saw for this exact reservation is recorded so the
 * summary can tell when the recalculated price differs; started without one
 * (e.g. from a duration chosen without a shown price), any earlier estimate is cleared.
 */
export function useStartStandardReservation(): (lastPriceEstimate?: LastPriceEstimate) => void {
  const navigate = useNavigate();
  const setSelectedPriceRuleId = useParkingSearchStore((state) => state.setSelectedPriceRuleId);
  const setLastPriceEstimate = useParkingSearchStore((state) => state.setLastPriceEstimate);

  return useCallback(
    (lastPriceEstimate?: LastPriceEstimate) => {
      setSelectedPriceRuleId(undefined);
      setLastPriceEstimate(lastPriceEstimate);
      navigate(ROUTE_PATHS[RouteName.RESERVATION_FLOW]);
    },
    [navigate, setSelectedPriceRuleId, setLastPriceEstimate]
  );
}
